export interface IScanIndexProgressionCondition {
  (scanIndex: number, item: any): boolean;
}

export interface IReflowOrderProgressionCondition {
  (scanIndex: number): Reflow;
}

export interface IProgressionConstructor {
  new (source: any, auxiliary?: any): Progression;
}

export interface IProgression {
  source: any;
  auxiliary: any;
  index: number;
  length: number;
  ordering: number[] | string[];

  isValid(): boolean;
  current(): any;
  previous(): any;
  hasPrevious(): boolean;
  peekPrevious(): any;
  scanPreviousIndex(condition: IScanIndexProgressionCondition): number;
  peekPreviousIndex(): number;
  next(): any;
  hasNext(): boolean;
  peekNext(): any;
  scanNextIndex(condition: IScanIndexProgressionCondition): number;
  peekNextIndex(): number;
  first(): any;
  isFirst(): boolean;
  isAfterFirst(): boolean;
  isBeforeFirst(): boolean;
  peekFirst(): any;
  peekFirstIndex(): number;
  last(): any;
  isLast(): boolean;
  isAfterLast(): boolean;
  isBeforeLast(): boolean;
  peekLast(): any;
  peekLastIndex(): number;
  size(): number;
  scanIndex(condition: IScanIndexProgressionCondition, progressionIncrement: number): number;
  seekItem(index: number): any;
  peekItem(index: number): any;
  isIndexBounded(index: number): boolean;
  queryItem(index: number): any;
  queryItemByKey(key: number | string): any;
  queryIndexByKey(key: number | string): number;
  queryFirstIndex(): number;
  queryLastIndex(): number;
  queryNextIndex(index: number): number;
  queryPreviousIndex(index: number): number;
  reflowWith(reflowName: string | Reflow): Progression;
  reflow(reflowName: string): Reflow;
  toString(): string;
}

export interface IReflow {
  rulesReflowIndex(progression: Progression, requestedIndex: number): number;
  ruleAt(index: number, orderFn: IReflowOrderProgressionCondition): Reflow;
  rule(orderFn: IReflowOrderProgressionCondition): Reflow;
}

export class Progression implements IProgression {
  private _activeReflow: Reflow | undefined;
  private _reflows: Reflow[];
  private _ordering: number[] | string[];
  private _index: number;
  private _auxiliary: any;
  private _source: any;

  constructor(source: any, auxiliary: any = {}) {
    this._activeReflow = undefined;
    this._auxiliary = auxiliary;
    this._reflows = [];
    this.source = source;
    this.index = this.peekFirstIndex();
  }

  /** @return true if item is an object */
  static isInvalidItem(item: any): boolean {
    return item === Progression.invalidItem();
  }

  /** @return true if index is OUT_OF_BOUNDS */
  static isOutOfBounds(index: number): boolean {
    return index === Progression.outOfBoundsIndex();
  }

  static invalidItem(): void {
    return undefined;
  }

  static outOfBoundsIndex(): number {
    return -1;
  }

  static create(source: any): Progression {
    return new Progression(source);
  }

  get auxiliary(): any {
    return this._auxiliary;
  }

  /** @return the source object the Progression iterates over */
  get source(): any {
    return this._source;
  }

  /** assign the source object the Progression iterates over */
  set source(externalSource: any) {
    if (_.isArray(externalSource)) {
      this._source = externalSource;
    } else if (_.isPlainObject(externalSource)) {
      this._source = externalSource;
    } else if (_.isNumber(externalSource)) {
      this._source = _.times(externalSource);
    } else {
      this._source = [];
    }

    this._ordering = _.keys(this._source);
  }

  /** @return {Number} current valid index */
  get index(): number {
    return this._index;
  }

  get ordering(): number[] | string[] {
    return this._ordering;
  }

  /**
   * @param requestedIndex index to change to
   */
  set index(requestedIndex: number) {
    if (!_.isNumber(requestedIndex)) {
      return;
    }

    if (!!this._activeReflow) {
      requestedIndex = this._activeReflow.rulesReflowIndex(this, requestedIndex);
    }

    if (requestedIndex < this.peekFirstIndex()) {
      this._index = this.peekFirstIndex();
    } else if (requestedIndex > this.peekLastIndex()) {
      this._index = this.peekLastIndex();
    } else {
      this._index = requestedIndex;
    }
  }

  /** @return true if source has values */
  isValid(): boolean {
    return this._ordering.length > 0;
  }

  /** @return the item at the current index */
  current(): any {
    return this.queryItem(this.index);
  }

  /** @return the item at the previous index and advance the index (or first, if the index is too low) */
  previous(): any {
    return this.seekItem(this.peekPreviousIndex());
  }

  /** @return true, if a previous item exists */
  hasPrevious(): boolean {
    return this.peekPreviousIndex() >= this.peekFirstIndex();
  }

  /** @return the item at the previous index (or OUT_OF_BOUNDS, if too low) */
  peekPrevious(): any {
    return this.peekItem(this.peekPreviousIndex());
  }

  /**
   * search from current index until condition returns true
   * @param condition a function called (index, item), if true, then we have found our item
   * @return the first previous index satisfying the condition
   */
  scanPreviousIndex(condition: IScanIndexProgressionCondition): number {
    return this.scanIndex(condition, -1);
  }

  /** @return {Number} the previous index */
  peekPreviousIndex(): number {
    return this.queryPreviousIndex(this.index);
  }

  /** @return the item at the next index and advance the index (or last, if the index is too high) */
  next(): any {
    return this.seekItem(this.peekNextIndex());
  }

  /** @return {Boolean} true, if next item exists */
  hasNext(): boolean {
    return this.peekNextIndex() <= this.peekLastIndex();
  }

  /** @return the item at the next index (or OUT_OF_BOUNDS, if too high) */
  peekNext(): any {
    return this.peekItem(this.peekNextIndex());
  }

  /**
   * search from current index until condition returns true
   * @param condition a function called (index, item), if true, then we have found our item
   * @return the first next index satisfying the condition
   */
  scanNextIndex(condition: IScanIndexProgressionCondition): number {
    return this.scanIndex(condition, 1);
  }

  /** @return the next index */
  peekNextIndex(): number {
    return this.queryNextIndex(this.index);
  }

  /** @return the first item and advance the index to the first index */
  first(): any {
    return this.seekItem(this.peekFirstIndex());
  }

  /** @return {Boolean} true, if index is at the first index */
  isFirst(): boolean {
    return this.index === this.peekFirstIndex();
  }

  /** @return true, if index is after first index */
  isAfterFirst(): boolean {
    return this.index > this.peekFirstIndex();
  }

  /** @return true, if index is before first index */
  isBeforeFirst(): boolean {
    return this.index < this.peekFirstIndex();
  }

  /** @alias Progression#first */
  peekFirst(): any {
    return this.peekItem(this.peekFirstIndex());
  }

  /** @return {Number} the first index (or -1 if Progression is empty) */
  peekFirstIndex(): number {
    return this.queryFirstIndex();
  }

  /** @return the last item and advance the index to the last index */
  last(): any {
    return this.seekItem(this.peekLastIndex());
  }

  /** @return true, if index is at the last index */
  isLast(): boolean {
    return this.index === this.peekLastIndex();
  }

  /** @return true, if index is after last index (if true, then the Progression is in error) */
  isAfterLast(): boolean {
    return this.index > this.peekLastIndex();
  }

  /** @return true, if index is before last index */
  isBeforeLast(): boolean {
    return this.index < this.peekLastIndex();
  }

  /** @alias Progression#last */
  peekLast(): any {
    return this.peekItem(this.peekLastIndex());
  }

  /** @return {Boolean} the last index (or -1 if Progression is empty) */
  peekLastIndex(): number {
    return this.queryLastIndex();
  }

  /** @return {Number} the length of the progression */
  get length(): number {
    return this._ordering.length;
  }

  /** @alias Progression#length */
  size(): number {
    return this.length;
  }

  /**
   * search from current index until condition returns true
   * @param condition a function called (index, item), if true, then we have found our item
   * @param progressionIncrement to move along the progression
   * @return the first index satisfying the condition
   */
  scanIndex(condition: IScanIndexProgressionCondition, progressionIncrement: number): number {
    progressionIncrement = _.isUndefined(progressionIncrement) ? 1 : progressionIncrement;

    let scanIndex: number = progressionIncrement > 0 ? this.peekNextIndex() : this.peekPreviousIndex();

    while (this.isIndexBounded(scanIndex)) {
      if (condition.call(this, scanIndex, this.peekItem(scanIndex))) {
        return scanIndex;
      }

      scanIndex += progressionIncrement;
    }

    return Progression.outOfBoundsIndex();
  }

  /** @return item at the current index (or bounded to first or last if the index provided is out of bounds) and change the current index */
  seekItem(index: number): any {
    this.index = index;
    return this.queryItem(this.index);
  }

  /** @return item at the current index or undefined, if the index is out of bounds */
  peekItem(index: number): any {
    return this.queryItem(index);
  }

  /** @return true, if index is inside of first/last index */
  isIndexBounded(index: number): boolean {
    return index >= this.queryFirstIndex() && index <= this.queryLastIndex();
  }

  /**
   * lookup item at index
   * @access protected
   * @return the source object for the current index or undefined if no source or index out of bounds
   */
  queryItem(index: number): any {
    return this.isValid() && this.isIndexBounded(index)
      ? this.source[this._ordering[index]]
      : Progression.invalidItem();
  }

  /**
   * lookup item by key
   * @return the source object for the index corresponding to given key or undefined if no source or index out of bounds
   */
  queryItemByKey(key: number | string): any {
    return this.queryItem(this.queryIndexByKey(key));
  }

  /**
   * lookup index by original key
   * @return the index of the source object for the index corresponding to given key or -1 if no source or index out of bounds
   */
  queryIndexByKey(key: number | string): number {
    return _.indexOf<number | string>(this.ordering, key);
  }

  /**
   * @access protected
   * @return first index
   */
  queryFirstIndex(): number {
    return this.length > 0 ? 0 : Progression.outOfBoundsIndex();
  }

  /**
   * @access protected
   * @return last index
   */
  queryLastIndex(): number {
    return this.length - 1;
  }

  /**
   * @access protected
   * @return next index from Progression#index
   */
  queryNextIndex(index: number): number {
    return index + 1;
  }

  /**
   * @access protected
   * @return previous index from Progression#index
   */
  queryPreviousIndex(index: number): number {
    return index - 1;
  }

  /**
   * assign the current reflow ruleset
   * @param reflowName the name of the reflow
   * @return the current progression
   */
  reflowWith(reflowName: string | Reflow): Progression {
    if (_.isString(reflowName)) {
      this._activeReflow = this._reflows[reflowName as string];
    } else if (reflowName instanceof Reflow) {
      this._activeReflow = reflowName;
    } else if (_.isUndefined(reflowName)) {
      this._activeReflow = undefined;
    }

    return this;
  }

  /**
   * create a new reflow and assign it as the current reflow
   * @param reflowName the name of the reflow
   * @return a new reflow
   */
  reflow(reflowName: string): Reflow {
    const reflow: Reflow = (this._activeReflow = new Reflow());

    if (_.isString(reflowName)) {
      this._reflows[reflowName] = reflow;
    }

    return reflow;
  }

  /**
   * @return {String} source of progression
   */
  toString(): string {
    return [JSON.stringify(this._source), this.index, JSON.stringify(this.current())].join(' | ');
  }
}

export class Reflow implements IReflow {
  private _rules: Function[] = [];

  /** @return {Number} the new index after a reflow */
  rulesReflowIndex(progression: Progression, requestedIndex: number): number {
    const rules: Function[] = this._rules;
    const requestedItem: any = progression.queryItem(requestedIndex);

    let i: number = rules.length;
    let value: any;

    // NOTE we reverse iterate so we can use push, in the reflow method, and decrement i, for performance
    while (i--) {
      value = rules[i].call(progression, requestedIndex, requestedItem);

      if (Progression.isOutOfBounds(value)) {
        return progression.index;
      }
      // NOTE undefined means the reflow did not have an opinion about this index
      else if (!_.isUndefined(value)) {
        return value;
      }
    }
    return requestedIndex;
  }

  /**
   * add a reflow rule at a specific index (assignment index)
   * @param {Number} index the index to control flow at
   * @param {Function} orderFn the function to control order with
   * @note allows index to be out of bounds because the progression may grow
   */
  ruleAt(index: number, orderFn: IReflowOrderProgressionCondition): Reflow {
    return this.rule((requestedIndex: number) => {
      if (requestedIndex === index) {
        return orderFn.call(this, requestedIndex);
      }
    });
  }

  /**
   * add a reflow rule with precendence above all previous reflow rules
   * @param {Function} orderFn the function to control order with
   */
  rule(orderFn: IReflowOrderProgressionCondition): Reflow {
    this._rules.push(orderFn);
    return this;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/utility-js-progression/src/main.ts